import App from '@js/App';
import { PagePath } from '@routing/appUrls';
import { lazy, Suspense } from 'react';
import lazyRetry, { LazyRetryNames } from '@utils/lazyRetry';
import LoginPage from '@components/pages/loginPage/LoginPage';
import ProtectedRoute from '@routing/components/ProtectedRoute';
import ErrorBoundary from '@components/features/errorBoundary/ErrorBoundary';
import { createBrowserRouter, Navigate } from 'react-router';
import ProjectContextRoute from '@routing/components/ProjectContextRoute';

const SettingsPage = lazy(() =>
    lazyRetry(
        () => import('@components/pages/settingsPage/SettingsPage'),
        LazyRetryNames.Settings,
    ),
);
const SelectProjectPage = lazy(() =>
    lazyRetry(
        () => import('@components/pages/selectProjectPage/SelectProjectPage'),
        LazyRetryNames.SelectProject,
    ),
);
const ProjectPage = lazy(() =>
    lazyRetry(
        () => import('@components/pages/projectPage/ProjectPage'),
        LazyRetryNames.ProjectPage,
    ),
);
const SupplierPage = lazy(() =>
    lazyRetry(
        () => import('@components/pages/supplierPage/SupplierPage'),
        LazyRetryNames.SupplierPage,
    ),
);
const OrderPage = lazy(() =>
    lazyRetry(
        () => import('@components/pages/orderPage/OrderPage'),
        LazyRetryNames.OrderPage,
    ),
);
const ProjectLibraryPage = lazy(() =>
    lazyRetry(
        () => import('@components/pages/projectLibraryPage/ProjectLibraryPage'),
        LazyRetryNames.ProjectLibraryPage,
    ),
);
const ProjectMembersPage = lazy(() =>
    lazyRetry(
        () => import('@components/pages/projectMembers/ProjectMembers'),
        LazyRetryNames.ProjectMembers,
    ),
);
const PriorityOrdersPage = lazy(() =>
    lazyRetry(
        () => import('@components/pages/priorityOrdersPage/PriorityOrdersPage'),
        LazyRetryNames.PriorityOrdersPage,
    ),
);

const router = createBrowserRouter([
    {
        path: PagePath.login,
        element: (
            <Suspense>
                <LoginPage />
            </Suspense>
        ),
    },
    {
        path: '/',
        errorElement: <ErrorBoundary />,
        element: (
            <ProtectedRoute>
                <App />
            </ProtectedRoute>
        ),
        children: [
            {
                path: PagePath.settings,
                element: (
                    <Suspense>
                        <SettingsPage />
                    </Suspense>
                ),
            },
            {
                path: PagePath.dashboard,
                element: <Navigate to={PagePath.projectPage} replace />,
            },
            {
                path: PagePath.selectProject,
                element: (
                    <Suspense>
                        <SelectProjectPage />
                    </Suspense>
                ),
            },
            {
                path: PagePath.projectPage,
                element: (
                    <Suspense>
                        <ProjectContextRoute>
                            <ProjectPage />
                        </ProjectContextRoute>
                    </Suspense>
                ),
            },
            {
                path: PagePath.supplierPage,
                element: (
                    <Suspense>
                        <ProjectContextRoute>
                            <SupplierPage />
                        </ProjectContextRoute>
                    </Suspense>
                ),
            },
            {
                path: PagePath.orderPage,
                element: (
                    <Suspense>
                        <ProjectContextRoute>
                            <OrderPage />
                        </ProjectContextRoute>
                    </Suspense>
                ),
            },
            {
                path: PagePath.projectLibraryPage,
                element: (
                    <Suspense>
                        <ProjectContextRoute>
                            <ProjectLibraryPage />
                        </ProjectContextRoute>
                    </Suspense>
                ),
            },
            {
                path: PagePath.projectMembers,
                element: (
                    <Suspense>
                        <ProjectContextRoute>
                            <ProjectMembersPage />
                        </ProjectContextRoute>
                    </Suspense>
                ),
            },
            {
                path: PagePath.priorityOrdersPage,
                element: (
                    <Suspense>
                        <ProjectContextRoute>
                            <PriorityOrdersPage />
                        </ProjectContextRoute>
                    </Suspense>
                ),
            },
        ],
    },
]);

export default router;
